// Copyright 2023 Sorama B.V.
import { customElement } from "aurelia-framework";
import { Chart, PointStyle } from "chart.js";
import { Point2D } from "datatypes/SoramaLocalApi";
import { SRIData } from "datatypes/SpecialMeasurement";
import { ReportTemplate } from "../ReportTemplate";

@customElement("sri-report-template")
export class SriReportTemplate extends ReportTemplate<SRIData> {
    private chartRef: HTMLCanvasElement;

    // ref curve has report from  100 to 3150, so null in other places
    private referenceCurve = [
        null,
        null,
        null,
        33,
        36,
        39,
        42,
        45,
        48,
        51,
        52,
        53,
        54,
        55,
        56,
        56,
        56,
        56,
        56,
        null,
        null,
    ];
    private shiftedReferenceCurve: number[] = [...this.referenceCurve];
    private xAxisFreq = [
        50, 63, 80, 100, 125, 160, 200, 250, 315, 400, 500, 630, 800, 1000, 1250, 1600, 2000, 2500, 3150, 4000, 5000,
    ];
    public SoundReductionIndex = "soundReductionIndex";

    private soundReductionIndexOctaveThird: Point2D[] = [];
    private soundReductionIndexDataOctaveOne: Point2D[] = [];

    private oneThirdOctaveChunks = [];
    private oneOneOctaveChunks = [];
    private xreportOctaveOne = [63, 125, 250, 500, 1000, 2000, 4000];
    protected roomVolume: number;
    protected surfaceArea: number;
    protected fieldMeasurementValue: string;
    private octaveOne: number[];
    private octaveThird: number[];

    attached() {
        this.drawChart(this.octaveThird, this.shiftedReferenceCurve, this.octaveOne);
    }

    public reportChanged() {
        if (!this.report) return;

        this.cleanUpUIView();

        // 1/3 octave band report
        this.calculateOctaveThreeBand();

        // 1/1 octave band report
        this.calculateOctaveOneBand();

        this.soundReductionIndexDataForView();

        // room volume
        if (this.data.roomVolume) {
            this.roomVolume = this.data.roomVolume;
        } else if (this.data.lrDimension1) {
            this.roomVolume = this.data.lrDimension1 * this.data.lrDimension2 * this.data.lrDimension3;
        } else {
            this.roomVolume = undefined;
        }

        // surface area
        if (this.data.roomSurfaceArea) {
            this.surfaceArea = this.data.roomSurfaceArea;
        } else if (this.data.swDimension1) {
            this.surfaceArea = this.data.swDimension1 * this.data.swDimension2;
        } else {
            this.surfaceArea = undefined;
        }

        this.fieldMeasurementValue = this.data.isFieldMeasurement ? "R'" : "R";

        this.plotGraph();

        if (this.data.shiftedReference) {
            this.shiftedReferenceCurve = [null, null, null, ...this.data.shiftedReference, null, null];
        }
    }

    private cleanUpUIView(): void {
        this.roomVolume = undefined;
        this.fieldMeasurementValue = undefined;

        this.oneThirdOctaveChunks.splice(0, this.oneThirdOctaveChunks.length);
        this.oneOneOctaveChunks.splice(0, this.oneOneOctaveChunks.length);
        this.soundReductionIndexDataOctaveOne.splice(0, this.soundReductionIndexDataOctaveOne.length);
        this.soundReductionIndexOctaveThird.splice(0, this.soundReductionIndexOctaveThird.length);
    }

    protected octaveStep = (x: number): number => {
        const rOctaveStep = 10 ** (-x / 10) / 3;
        return rOctaveStep;
    };

    // Made from matlab script formula from Achiel
    protected calculateOctaveThreeBand = () => {
        // 1/3 octave
        this.data.soundReductionIndex.forEach((yAxisreport: number, index) => {
            this.soundReductionIndexOctaveThird.push({ x: this.xAxisFreq[index], y: yAxisreport });
        });
    };

    // Made from matlab script formula from Achiel
    protected calculateOctaveOneBand = () => {
        //[0,3,6,9,12,15,18] : [63, 125, 250, 500, 1000, 2000, 4000];
        for (let index = 0; index < this.soundReductionIndexOctaveThird.length; index = index + 3) {
            const result =
                this.octaveStep(this.soundReductionIndexOctaveThird[index].y) +
                this.octaveStep(this.soundReductionIndexOctaveThird[index + 1].y) +
                this.octaveStep(this.soundReductionIndexOctaveThird[index + 2].y);

            const resultY = (-10 * Math.log10(result)) % this.soundReductionIndexOctaveThird[index + 1].x;

            // add null report for band report we do not data to show
            this.soundReductionIndexDataOctaveOne.push({ x: this.xAxisFreq[index], y: null });
            this.soundReductionIndexDataOctaveOne.push({ x: this.xAxisFreq[index + 1], y: resultY });
            this.soundReductionIndexDataOctaveOne.push({ x: this.xAxisFreq[index + 2], y: null });
        }
    };

    // Arrange the array into a single row to show in html
    protected soundReductionIndexDataForView(): void {
        const rowSize = 7;
        const colwSize = 3;

        // with chunks, we print the whole array in a single row
        for (let k = 0; k < colwSize; k++) {
            const arr = [];
            for (let i = k; i < this.soundReductionIndexOctaveThird.length; i = i + colwSize) {
                arr.push(this.soundReductionIndexOctaveThird[i]);
            }
            this.oneThirdOctaveChunks.splice(k, 0, arr);
        }

        // chunks for 1/1 octave that shows only 7 band report [63, 125, 250, 500, 1000, 2000, 4000]
        const filterOctaveOne = this.soundReductionIndexDataOctaveOne.filter((element) =>
            this.xreportOctaveOne.includes(element.x)
        );
        for (let i = 0; i < filterOctaveOne.length; i += rowSize) {
            const row = filterOctaveOne.slice(i, i + rowSize).sort();
            this.oneOneOctaveChunks.splice(i, 0, row);
        }
    }

    protected plotGraph(): void {
        // Only show the band report in UI as in x axis
        const filterOctaveThird = this.soundReductionIndexOctaveThird.filter((element) =>
            this.xAxisFreq.includes(element.x)
        );
        const mappedfilterOctaveThird = filterOctaveThird.map((item) => item.y);

        // One Octave report
        const filterOctaveOne = this.soundReductionIndexDataOctaveOne.filter((element) =>
            this.xAxisFreq.includes(element.x)
        );
        const mappedfilterOctaveOne = filterOctaveOne.map((item) => item.y);

        this.octaveThird = mappedfilterOctaveThird;
        this.octaveOne = mappedfilterOctaveOne;
    }

    protected drawChart(octaveThree: number[], refCurve: number[], octaveOne: number[]): void {
        let yLabel: string;
        if (this.data.isSTC) {
            // For STC variant, don't show the octave band
            octaveOne = [];
            yLabel = "Apparent Transmission Loss ATL[dB]";
        } else {
            if (this.data.isFieldMeasurement) {
                yLabel = "Sound Reduction Index R'[dB]";
            } else {
                yLabel = "Sound Reduction Index R[dB]";
            }
        }

        // Get y-axis limits from data bounds (rounded to nearest 5)
        const minValue = Math.min(...octaveThree, ...refCurve, ...octaveOne);
        const maxValue = Math.max(...octaveThree, ...refCurve, ...octaveOne);

        const minBound = 5 * (minValue > 0 ? Math.ceil(minValue / 5) : Math.floor(minValue / 5));
        const maxBound = 5 * (maxValue > 0 ? Math.ceil(maxValue / 5) : Math.floor(maxValue / 5));

        try {
            new Chart(this.chartRef, {
                type: "line",
                data: {
                    labels: this.xAxisFreq,
                    datasets: [
                        {
                            fill: false,
                            backgroundColor: "black",
                            lineTension: 0,
                            borderColor: "black",
                            data: octaveThree,
                            pointStyle: {} as PointStyle,
                            pointRadius: 0,
                        },
                        {
                            fill: false,
                            backgroundColor: "black",
                            borderColor: "black",
                            lineTension: 0,
                            data: refCurve,
                            borderDash: [10, 5],
                            pointStyle: {} as PointStyle,
                            pointRadius: 0,
                            pointHoverRadius: 0,
                        },
                        {
                            fill: false,
                            backgroundColor: "white",
                            borderColor: "white",
                            lineTension: 0,
                            data: octaveOne,
                            pointStyle: "star",
                            pointRadius: 5,
                            pointBorderColor: "rgb(0, 0, 0)",
                            borderWidth: 0.5,
                        },
                    ],
                },
                options: {
                    animation: {
                        duration: 0,
                    },
                    aspectRatio: 1 / 2,
                    legend: { display: false },
                    maintainAspectRatio: false,
                    scales: {
                        xAxes: [
                            {
                                scaleLabel: {
                                    display: true,
                                    labelString: "Frequency [Hz]",
                                },
                                offset: false, // to start from 50
                                ticks: {
                                    display: true,
                                    beginAtZero: true,
                                    // indexes are the ones we want to show the grid lines for [50, (63, 1), 80, 100, (125, 4), 160, 200, (250, 7),  and so on....., 5000];
                                    callback: function (value, index, report) {
                                        if (
                                            index == 1 ||
                                            index == 4 ||
                                            index == 7 ||
                                            index == 10 ||
                                            index == 13 ||
                                            index == 16 ||
                                            index == 19
                                        ) {
                                            return value;
                                        }
                                    },
                                },
                            },
                        ],
                        yAxes: [
                            {
                                ticks: {
                                    // Always include Y = 0 line
                                    min: Math.min(0, minBound),
                                    max: Math.max(0, maxBound),
                                },
                                scaleLabel: {
                                    display: true,
                                    labelString: yLabel,
                                },
                            },
                        ],
                    },
                },
            });
        } catch (error) {
            console.log(error);
        }
    }
}
